import { useCallback, useEffect, useRef, useState } from 'react';
import { networkManager } from '../../systems/NetworkManager';
import { friendlyCorpError } from './errors';
import type { CorpDetail, CorpSummary } from './types';

interface ApplyArgs { corp: string }
interface CreateContractArgs {
  name: string;
  tag: string;
  managerName: string;
  totalCapital: number;
  owners: { username: string; percentage: number }[];
}
interface WithdrawArgs { corpId: string; amount: number }
interface SetPermissionArgs { corpId: string; targetUsername: string; flags: number }
interface RespondApplicationArgs { corpId: string; applicantName: string; decision: 'accept' | 'reject' }
interface KickMemberArgs { corpId: string; targetName: string }
interface SetDistributionArgs { corpId: string; percentage: number; periodDays: number }
interface StartVoteArgs { corpId: string; nomineeName: string }
interface CastVoteArgs { voteId: string; decision: 'remove' | 'keep' }

export interface UseCorporationDataOptions {
  /** Called once after the hook attaches its subscriptions (use to trigger an initial fetch). */
  autoFetch?: boolean;
  /** Fired when a corp creation contract was successfully sent. */
  onCreated?: () => void;
  /** Fired when the player successfully left their corp. */
  onLeft?: () => void;
  /** Fired when a corp was successfully dissolved. Payload includes refund amount + corp display name. */
  onDissolved?: (info: { refundedCredits: number; corpName: string; corpTag: string }) => void;
}

export interface UseCorporationDataResult {
  corps: CorpSummary[] | null;
  detail: CorpDetail | null;
  error: string | null;
  busy: boolean;
  applyMsg: string | null;
  setError: (msg: string | null) => void;
  clearError: () => void;
  clearApplyMsg: () => void;
  refreshList: () => void;
  openCorp: (corpId: string) => void;
  closeDetail: () => void;
  apply: (args: ApplyArgs) => void;
  leave: () => void;
  createContract: (args: CreateContractArgs) => void;
  withdraw: (args: WithdrawArgs) => void;
  setPermission: (args: SetPermissionArgs) => void;
  respondApplication: (args: RespondApplicationArgs) => void;
  kickMember: (args: KickMemberArgs) => void;
  setDistribution: (args: SetDistributionArgs) => void;
  startVote: (args: StartVoteArgs) => void;
  castVote: (args: CastVoteArgs) => void;
  dissolve: (args: { corpId: string }) => void;
}

function send(message: string, payload: object = {}) {
  const room: any = (networkManager as any).room;
  room?.send(message, payload);
}

export function useCorporationData(opts: UseCorporationDataOptions = {}): UseCorporationDataResult {
  const { autoFetch = true, onCreated, onLeft, onDissolved } = opts;

  const [corps, setCorps] = useState<CorpSummary[] | null>(null);
  const [detail, setDetail] = useState<CorpDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [applyMsg, setApplyMsg] = useState<string | null>(null);

  // Keep latest detailId in a ref so the message handlers can refresh the
  // currently open corp without re-subscribing whenever detail changes.
  const detailIdRef = useRef<string | null>(null);
  detailIdRef.current = detail?.id ?? null;

  const onCreatedRef = useRef(onCreated);
  onCreatedRef.current = onCreated;
  const onLeftRef = useRef(onLeft);
  onLeftRef.current = onLeft;
  const onDissolvedRef = useRef(onDissolved);
  onDissolvedRef.current = onDissolved;

  const refreshList = useCallback(() => send('getMyCorporations'), []);
  const openCorp = useCallback((corpId: string) => send('getCorporation', { corpId }), []);
  const closeDetail = useCallback(() => setDetail(null), []);

  useEffect(() => {
    const room: any = (networkManager as any).room;
    if (!room) return;

    const unsubs: Array<() => void> = [];

    const refreshOpenAndList = (msg: any) => {
      setBusy(false);
      if (!msg.success) { setError(friendlyCorpError(msg.error)); return; }
      setError(null);
      if (detailIdRef.current) openCorp(detailIdRef.current);
      refreshList();
    };

    unsubs.push(room.onMessage('myCorporations', (msg: { corps: CorpSummary[] }) => {
      setCorps(msg.corps ?? []);
    }));
    unsubs.push(room.onMessage('corporationDetail', (msg: CorpDetail) => {
      setDetail(msg);
    }));
    unsubs.push(room.onMessage('createCorporationResult', (msg: any) => {
      setBusy(false);
      if (!msg.success) { setError(friendlyCorpError(msg.error)); return; }
      setError(null);
      refreshList();
      onCreatedRef.current?.();
    }));
    unsubs.push(room.onMessage('withdrawCorpBankResult', (msg: any) => {
      setBusy(false);
      if (!msg.success) { setError(friendlyCorpError(msg.error)); return; }
      setError(null);
      if (detailIdRef.current) openCorp(detailIdRef.current);
    }));
    unsubs.push(room.onMessage('setCorpPermissionResult', (msg: any) => {
      setBusy(false);
      if (!msg.success) { setError(friendlyCorpError(msg.error)); return; }
      setError(null);
      if (detailIdRef.current) openCorp(detailIdRef.current);
    }));
    unsubs.push(room.onMessage('applyToCorpResult', (msg: any) => {
      setBusy(false);
      if (!msg.success) {
        setError(friendlyCorpError(msg.error));
        return;
      }
      setError(null);
      setApplyMsg('Application sent.');
      refreshList();
    }));
    unsubs.push(room.onMessage('leaveCorpResult', (msg: any) => {
      setBusy(false);
      if (!msg.success) { setError(friendlyCorpError(msg.error)); return; }
      setError(null);
      setDetail(null);
      refreshList();
      onLeftRef.current?.();
    }));
    unsubs.push(room.onMessage('dissolveCorporationResult', (msg: any) => {
      setBusy(false);
      if (!msg.success) { setError(friendlyCorpError(msg.error)); return; }
      setError(null);
      setDetail(null);
      refreshList();
      onDissolvedRef.current?.({
        refundedCredits: Number(msg.refundedCredits) || 0,
        corpName: String(msg.corpName ?? ''),
        corpTag: String(msg.corpTag ?? ''),
      });
    }));
    unsubs.push(room.onMessage('respondCorpApplicationResult', refreshOpenAndList));
    unsubs.push(room.onMessage('kickCorpMemberResult', refreshOpenAndList));
    unsubs.push(room.onMessage('setCorpDistributionResult', refreshOpenAndList));
    unsubs.push(room.onMessage('startManagerVoteResult', refreshOpenAndList));
    unsubs.push(room.onMessage('castManagerVoteResult', refreshOpenAndList));

    if (autoFetch) refreshList();

    return () => {
      for (const u of unsubs) {
        try { u(); } catch { /* noop */ }
      }
    };
  }, [autoFetch, openCorp, refreshList]);

  const apply = useCallback((args: ApplyArgs) => {
    setBusy(true); setApplyMsg(null); send('applyToCorp', args);
  }, []);
  const leave = useCallback(() => {
    setBusy(true); send('leaveCorp');
  }, []);
  const createContract = useCallback((args: CreateContractArgs) => {
    setBusy(true); send('createCorporationContract', args);
  }, []);
  const withdraw = useCallback((args: WithdrawArgs) => {
    setBusy(true); send('withdrawFromCorpBank', args);
  }, []);
  const setPermission = useCallback((args: SetPermissionArgs) => {
    setBusy(true); send('setCorpPermission', args);
  }, []);
  const respondApplication = useCallback((args: RespondApplicationArgs) => {
    setBusy(true); send('respondCorpApplication', args);
  }, []);
  const kickMember = useCallback((args: KickMemberArgs) => {
    setBusy(true); send('kickCorpMember', args);
  }, []);
  const setDistribution = useCallback((args: SetDistributionArgs) => {
    setBusy(true); send('setCorpDistribution', args);
  }, []);
  const startVote = useCallback((args: StartVoteArgs) => {
    setBusy(true); send('startManagerVote', args);
  }, []);
  const castVote = useCallback((args: CastVoteArgs) => {
    setBusy(true); send('castManagerVote', args);
  }, []);
  const dissolve = useCallback((args: { corpId: string }) => {
    setBusy(true); send('dissolveCorporation', args);
  }, []);

  return {
    corps, detail, error, busy, applyMsg,
    setError,
    clearError: useCallback(() => setError(null), []),
    clearApplyMsg: useCallback(() => setApplyMsg(null), []),
    refreshList, openCorp, closeDetail,
    apply, leave, createContract, withdraw, setPermission,
    respondApplication, kickMember, setDistribution, startVote, castVote,
    dissolve,
  };
}

export function localCorpUsername(): string {
  const room: any = (networkManager as any).room;
  return room?.state?.players?.get?.(room.sessionId)?.username ?? '';
}
